import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../context/AuthContext";
import MasterKeyModal from "../../components/common/MasterKeyModal";
import { toast } from "sonner";
import {
  AlertTriangle,
  Calculator,
  Check,
  Eye,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { formatBRL, formatPct } from "../../utils/format";

const EMPTY_EVALUATION = {
  nome: "",
  nome_proposta: "",
  custo: 0,
  venda: 0,
  unidade: "ponto",
  por_funcionario: false,
  mensal: false,
};

const markupPct = (custo, venda) => {
  const c = Number(custo || 0);
  const v = Number(venda || 0);
  return c > 0 ? ((v - c) / c) * 100 : 0;
};

const normalizeItem = (item) => ({
  ...EMPTY_EVALUATION,
  ...item,
  nome_proposta: String(item?.nome_proposta || item?.nome || ""),
});

const comparable = (item) => JSON.stringify({
  nome: String(item?.nome || "").trim(),
  nome_proposta: String(item?.nome_proposta || "").trim(),
  unidade: String(item?.unidade || "ponto").trim(),
  custo: Number(item?.custo || 0),
  venda: Number(item?.venda || 0),
  por_funcionario: !!item?.por_funcionario,
  mensal: !!item?.mensal,
});

function EvaluationEditor({ value, onChange }) {
  const setField = (key, next) => onChange({ ...value, [key]: next });
  const updateInternalName = (next) => {
    const previous = String(value.nome || "");
    const commercial = String(value.nome_proposta || "");
    onChange({
      ...value,
      nome: next,
      nome_proposta: !commercial || commercial === previous ? next : commercial,
    });
  };
  const preview = String(value.nome_proposta || value.nome || "").trim();

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,.75fr)]">
      <div className="space-y-5">
        <section className="rounded-xl border border-border bg-background p-4 sm:p-5">
          <div className="mb-4">
            <div className="text-sm font-semibold">Identificação da avaliação</div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Nome, unidade e forma de cálculo são cadastrados juntos. Não existe mais salvamento por linha.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Nome interno *</Label>
              <Input
                className="mt-1.5"
                value={value.nome}
                onChange={(e) => updateInternalName(e.target.value)}
                placeholder="Ex.: Dosimetria de ruído"
                autoFocus
                data-testid="fixed-editor-name"
              />
              <p className="mt-1.5 text-[11px] text-muted-foreground">Usado para localizar e administrar a avaliação dentro do sistema.</p>
            </div>
            <div>
              <Label>Unidade comercial *</Label>
              <Input
                className="mt-1.5"
                value={value.unidade}
                onChange={(e) => setField("unidade", e.target.value)}
                placeholder="ponto, medição, funcionário..."
                disabled={!!value.por_funcionario}
              />
              {value.por_funcionario && <p className="mt-1.5 text-[11px] text-muted-foreground">A unidade é controlada automaticamente por funcionário.</p>}
            </div>
            <div className="rounded-xl border border-border bg-muted/20 p-3">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Destino na proposta</div>
              <div className="mt-1 text-sm font-semibold text-primary">Avaliações</div>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">Avaliações fixas nunca são convertidas em treinamento. Treinamentos pertencem ao catálogo de Serviços e preços.</p>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-primary/20 bg-primary/[0.025] p-4 sm:p-5">
          <div className="mb-4 flex items-start gap-3">
            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Eye className="size-4" /></div>
            <div>
              <div className="text-sm font-semibold">Como aparece para o cliente</div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">O sistema usa este texto literalmente. Não adiciona “Avaliação de”, “Treinamento de” ou outro prefixo automático.</p>
            </div>
          </div>
          <Label>Nome exibido na proposta *</Label>
          <Textarea
            className="mt-1.5 min-h-[82px] resize-y"
            value={value.nome_proposta}
            onChange={(e) => setField("nome_proposta", e.target.value)}
            placeholder="Ex.: Avaliação quantitativa de exposição ocupacional ao ruído"
            data-testid="fixed-editor-commercial-name"
          />
          <div className="mt-3 rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Prévia na proposta</div>
            <div className="mt-1.5 text-sm font-semibold">{preview || "Digite o nome comercial acima"}</div>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-background p-4 sm:p-5">
          <div className="mb-4">
            <div className="text-sm font-semibold">Forma de cálculo</div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Defina se a quantidade vem do número de funcionários e se a cobrança se repete durante os meses do contrato.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={`flex cursor-pointer gap-3 rounded-xl border p-4 ${value.por_funcionario ? "border-primary/40 bg-primary/[0.05]" : "border-border"}`}>
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-primary"
                checked={!!value.por_funcionario}
                onChange={(e) => onChange({ ...value, por_funcionario: e.target.checked, ...(e.target.checked ? { unidade: "funcionário" } : {}) })}
              />
              <span><span className="block text-sm font-medium">Por funcionário</span><span className="mt-1 block text-xs text-muted-foreground">Na proposta, a quantidade acompanha automaticamente o número de funcionários do cliente.</span></span>
            </label>
            <label className={`flex cursor-pointer gap-3 rounded-xl border p-4 ${value.mensal ? "border-primary/40 bg-primary/[0.05]" : "border-border"}`}>
              <input type="checkbox" className="mt-0.5 size-4 accent-primary" checked={!!value.mensal} onChange={(e) => setField("mensal", e.target.checked)} />
              <span><span className="block text-sm font-medium">Cobrança mensal</span><span className="mt-1 block text-xs text-muted-foreground">Custo e venda são multiplicados pela duração contratual.</span></span>
            </label>
          </div>
        </section>
      </div>

      <section className="h-fit rounded-xl border border-border bg-background p-4 sm:p-5 xl:sticky xl:top-5">
        <div className="mb-4">
          <div className="text-sm font-semibold">Preço padrão</div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Preço e cálculo ficam na mesma ficha da avaliação, mas não alteram o texto comercial.</p>
        </div>
        <div className="space-y-4">
          <div><Label>Custo unitário</Label><Input className="mt-1.5" type="number" min="0" step="0.01" value={value.custo} onChange={(e) => setField("custo", Number(e.target.value || 0))} /></div>
          <div><Label>Preço de venda unitário</Label><Input className="mt-1.5" type="number" min="0" step="0.01" value={value.venda} onChange={(e) => setField("venda", Number(e.target.value || 0))} /></div>
          <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/35 p-3 text-center">
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Custo</div><div className="mt-1 text-xs font-semibold">{formatBRL(value.custo)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Venda</div><div className="mt-1 text-xs font-semibold">{formatBRL(value.venda)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Markup</div><div className="mt-1 text-xs font-semibold">{formatPct(markupPct(value.custo, value.venda))}</div></div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function FixedEvaluations() {
  const { masterUnlocked } = useAuth();
  const [items, setItems] = useState([]);
  const [showMaster, setShowMaster] = useState(false);
  const [query, setQuery] = useState("");
  const [billingFilter, setBillingFilter] = useState("all");
  const [editorMode, setEditorMode] = useState("");
  const [editingId, setEditingId] = useState("");
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const load = async () => {
    try {
      const rows = (await api.get("/fixed-evaluations")).data || [];
      setItems(rows.map(normalizeItem));
    } catch (e) {
      if (e.response?.status === 423) setShowMaster(true);
      else toast.error(e.response?.data?.detail || "Falha ao carregar avaliações fixas");
    }
  };

  useEffect(() => {
    if (masterUnlocked) load();
    else setShowMaster(true);
  }, [masterUnlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!draft && comparable(draft) !== comparable(baseline || EMPTY_EVALUATION);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("pt-BR");
    return items.filter((it) => {
      if (billingFilter === "employee" && !it.por_funcionario) return false;
      if (billingFilter === "monthly" && !it.mensal) return false;
      if (billingFilter === "single" && (it.por_funcionario || it.mensal)) return false;
      if (!needle) return true;
      return [it.nome, it.nome_proposta, it.unidade].some((value) => String(value || "").toLocaleLowerCase("pt-BR").includes(needle));
    });
  }, [items, query, billingFilter]);

  const summary = useMemo(() => ({
    total: items.length,
    employee: items.filter((x) => x.por_funcionario).length,
    monthly: items.filter((x) => x.mensal).length,
    priced: items.filter((x) => Number(x.venda || 0) > 0).length,
  }), [items]);

  const validate = (it) => {
    const name = String(it?.nome || "").trim();
    const commercial = String(it?.nome_proposta || "").trim();
    if (!name) return "Informe o nome interno da avaliação.";
    if (!commercial) return "Informe como a avaliação deve aparecer na proposta.";
    if (!String(it?.unidade || "").trim()) return "Informe a unidade comercial.";
    if (Number(it?.custo || 0) < 0 || Number(it?.venda || 0) < 0) return "Custo e venda não podem ser negativos.";
    const duplicate = items.find((row) => row.id !== editingId && String(row.nome || "").trim().toLocaleLowerCase("pt-BR") === name.toLocaleLowerCase("pt-BR"));
    if (duplicate) return "Já existe uma avaliação com este nome interno.";
    return "";
  };

  const payloadOf = (it) => ({
    nome: String(it.nome || "").trim(),
    nome_proposta: String(it.nome_proposta || it.nome || "").trim(),
    custo: Number(it.custo || 0),
    venda: Number(it.venda || 0),
    unidade: String(it.unidade || "ponto").trim(),
    por_funcionario: !!it.por_funcionario,
    mensal: !!it.mensal,
  });

  const requireCleanWorkspace = () => {
    if (!dirty) return true;
    toast.error("Há alterações não salvas nesta avaliação. Salve ou descarte a ficha antes de abrir outra.");
    return false;
  };

  const startNew = () => {
    if (!requireCleanWorkspace()) return;
    const fresh = normalizeItem(EMPTY_EVALUATION);
    setEditorMode("new");
    setEditingId("");
    setDraft(fresh);
    setBaseline(fresh);
    setDeleteConfirm("");
  };

  const startEdit = (item) => {
    if (editingId === item.id && editorMode === "edit") return;
    if (!requireCleanWorkspace()) return;
    const normalized = normalizeItem(item);
    setEditorMode("edit");
    setEditingId(item.id);
    setDraft(normalized);
    setBaseline(normalized);
    setDeleteConfirm("");
  };

  const closeEditor = () => {
    if (dirty) {
      toast.error("Há alterações não salvas. Use “Descartar alterações” se quiser sair sem salvar.");
      return;
    }
    setEditorMode("");
    setEditingId("");
    setDraft(null);
    setBaseline(null);
  };

  const discard = () => {
    setEditorMode("");
    setEditingId("");
    setDraft(null);
    setBaseline(null);
    toast("Alterações descartadas");
  };

  const saveWorkspace = async () => {
    if (!draft) return;
    const error = validate(draft);
    if (error) return toast.error(error);
    setSaving(true);
    try {
      if (editorMode === "new") {
        const r = await api.post("/fixed-evaluations", payloadOf(draft));
        const saved = normalizeItem(r.data);
        setItems((current) => [...current, saved].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")));
        toast.success("Avaliação criada e disponível nas propostas");
      } else if (editorMode === "edit" && editingId) {
        const r = await api.put(`/fixed-evaluations/${editingId}`, payloadOf(draft));
        const saved = normalizeItem(r.data);
        setItems((current) => current.map((row) => row.id === editingId ? saved : row));
        toast.success("Alterações da avaliação salvas");
      }
      setEditorMode("");
      setEditingId("");
      setDraft(null);
      setBaseline(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar avaliação");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (dirty) {
      toast.error("Salve ou descarte a ficha em edição antes de excluir outra avaliação.");
      return;
    }
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }
    try {
      await api.delete(`/fixed-evaluations/${id}`);
      setItems((current) => current.filter((it) => it.id !== id));
      setDeleteConfirm("");
      toast.success("Avaliação excluída");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir avaliação");
    }
  };

  const Metric = ({ value, label, icon: Icon }) => (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="size-4 text-primary" /> {label}</div>
      <div className="mt-2 text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );

  return (
    <>
      <Header
        title="Avaliações fixas e preços"
        subtitle="Uma ficha completa por avaliação, com texto comercial, cálculo e preço salvos de uma só vez"
        actions={(
          <Button onClick={startNew} disabled={editorMode === "new"} data-testid="add-fixed-eval-btn">
            <Plus className="size-4 mr-1" /> Nova avaliação
          </Button>
        )}
      />

      <div className="mx-auto w-full max-w-[1500px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="fixed-evals-page">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric value={summary.total} label="Avaliações cadastradas" icon={Calculator} />
          <Metric value={summary.employee} label="Por funcionário" icon={Users} />
          <Metric value={summary.monthly} label="Com cobrança mensal" icon={Calculator} />
          <Metric value={summary.priced} label="Com preço de venda" icon={Check} />
        </div>

        <div className="rounded-2xl border border-primary/20 bg-primary/[0.035] p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Check className="size-4" /></div>
            <div>
              <div className="font-medium">Sem salvamento por linha e sem confirmação nativa do navegador</div>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Abra uma avaliação, altere todos os campos e salve a ficha inteira. Avisos de alterações pendentes aparecem dentro do sistema.</p>
            </div>
          </div>
        </div>

        {editorMode && draft && (
          <section className="rounded-2xl border border-primary/30 bg-card shadow-sm" data-testid="fixed-workspace">
            <div className="flex flex-col gap-3 border-b border-border p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-primary">{editorMode === "new" ? "Nova avaliação" : "Editando avaliação"}</div>
                <h2 className="mt-1 text-lg font-semibold">{editorMode === "new" ? "Preencha a ficha da avaliação" : (baseline?.nome || "Avaliação")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">Nome, texto comercial, cálculo e preço são salvos juntos.</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={closeEditor} disabled={dirty}><X className="size-4 mr-1" /> Fechar ficha</Button>
            </div>

            <div className="p-5 sm:p-6"><EvaluationEditor value={draft} onChange={setDraft} /></div>

            <div className={`sticky bottom-0 z-10 flex flex-col gap-3 border-t p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:px-6 ${dirty ? "border-amber-500/25 bg-background/95" : "border-border bg-background/95"}`}>
              <div className="flex items-center gap-2 text-sm">
                {dirty ? <><AlertTriangle className="size-4 text-amber-600" /><span className="font-medium text-amber-700 dark:text-amber-400">Alterações ainda não salvas</span></> : <><Check className="size-4 text-emerald-600" /><span className="text-muted-foreground">Nenhuma alteração pendente</span></>}
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button type="button" variant="outline" onClick={dirty ? discard : closeEditor}>{dirty ? "Descartar alterações" : "Cancelar"}</Button>
                <Button type="button" onClick={saveWorkspace} disabled={saving || !dirty} data-testid="fixed-workspace-save"><Save className="size-4 mr-1" /> {saving ? "Salvando..." : editorMode === "new" ? "Criar avaliação" : "Salvar alterações"}</Button>
              </div>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-border bg-card p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative min-w-0 flex-1 lg:max-w-2xl"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar pelo nome, texto da proposta ou unidade..." /></div>
            <Select value={billingFilter} onValueChange={setBillingFilter}><SelectTrigger className="w-full lg:w-56"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todas</SelectItem><SelectItem value="employee">Por funcionário</SelectItem><SelectItem value="monthly">Mensais</SelectItem><SelectItem value="single">Valor único</SelectItem></SelectContent></Select>
          </div>
        </section>

        <div className="grid gap-3 lg:grid-cols-2">
          {filtered.map((it) => {
            const active = editorMode === "edit" && editingId === it.id;
            const confirming = deleteConfirm === it.id;
            return (
              <article key={it.id} className={`rounded-2xl border bg-card p-4 sm:p-5 ${active ? "border-primary/45 ring-1 ring-primary/10" : "border-border"}`} data-testid={`fixed-row-${it.id}`}>
                <div className="flex h-full flex-col gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">Avaliação</span>
                      {it.por_funcionario && <span className="rounded-md bg-muted px-2 py-1 text-[11px]">por funcionário</span>}
                      {it.mensal && <span className="rounded-md bg-muted px-2 py-1 text-[11px]">mensal</span>}
                    </div>
                    <div className="mt-2 text-base font-semibold">{it.nome_proposta || it.nome}</div>
                    {it.nome !== it.nome_proposta && <div className="mt-1 text-sm text-muted-foreground">Cadastro interno: {it.nome}</div>}
                    <div className="mt-1 text-xs text-muted-foreground">Unidade: {it.unidade}</div>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"><span>Custo {formatBRL(it.custo)}</span><span>Venda {formatBRL(it.venda)}</span><span>Markup {formatPct(markupPct(it.custo, it.venda))}</span></div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
                    <Button variant={active ? "secondary" : "outline"} size="sm" onClick={() => startEdit(it)} disabled={active}><Pencil className="size-4 mr-1" /> {active ? "Em edição" : "Abrir ficha"}</Button>
                    <Button variant={confirming ? "destructive" : "ghost"} size="sm" onClick={() => del(it.id)}>{confirming ? "Confirmar exclusão" : <><Trash2 className="size-4 mr-1 text-destructive" /> Excluir</>}</Button>
                    {confirming && <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm("")}>Cancelar</Button>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">Nenhuma avaliação corresponde aos filtros.</div>}
      </div>

      <MasterKeyModal open={showMaster} onOpenChange={setShowMaster} onUnlocked={load} reason="Os custos e preços das avaliações são protegidos pela senha mestra." />
    </>
  );
}
